// Убеждаемся, что плагины загружены перед использованием RxDB
import './plugins';

import { RxDB, getDatabase, cleanupLRUDocuments, recreateDatabase } from './database';
import { DocumentSchemaType, SyncStatus } from './schema';

/**
 * Класс для работы с документами в RxDB
 */
export class DocumentRepository {
  private db: RxDB | null = null;

  /**
   * Инициализирует репозиторий и подключается к базе данных
   */
  async initialize(): Promise<void> {
    if (!this.db) {
      try {
        this.db = await getDatabase();
      } catch (error) {
        console.error('Ошибка при инициализации базы данных:', error);
        // Попробуем пересоздать базу данных
        try {
          console.warn('Попытка пересоздания базы данных...');
          this.db = await recreateDatabase();
        } catch (recreateError) {
          console.error('Не удалось пересоздать базу данных:', recreateError);
          throw recreateError;
        }
      }
    }
  }

  /**
   * Получает документ по его ID
   * @param id ID документа
   * @returns Документ или null, если документ не найден
   */
  async getDocumentById(id: string): Promise<DocumentSchemaType | null> {
    if (!this.db) {
      await this.initialize();
    }

    const doc = await this.db!.documents.findOne({
      selector: {
        id
      }
    }).exec();

    if (!doc) {
      return null;
    }

    // Обновляем время последнего доступа с обработкой ошибок
    const now = Date.now();
    try {
      // Используем incremental update для предотвращения конфликтов
      await doc.incrementalUpdate({
        $set: {
          lastAccessed: now
        }
      });
    } catch (updateError) {
      // Если не удалось обновить lastAccessed, логируем ошибку, но продолжаем
      console.warn(`Не удалось обновить lastAccessed для документа ${id}:`, updateError);
      // Возвращаем документ без обновления lastAccessed
    }

    return doc.toJSON();
  }

  /**
   * Сохраняет документ (создает новый или обновляет существующий)
   * @param document Документ для сохранения
   * @returns Сохраненный документ
   */
  async saveDocument(document: Partial<DocumentSchemaType> & { id: string }): Promise<DocumentSchemaType> {
    if (!this.db) {
      await this.initialize();
    }

    console.log(`[DocumentRepository] Сохранение документа ${document.id}:`, {
      contentType: typeof document.content,
      contentLength: typeof document.content === 'string' ? document.content.length : 'N/A',
      contentPreview: typeof document.content === 'string' ? document.content.substring(0, 100) : document.content,
      hasContent: !!document.content
    });

    const now = Date.now();
    const existingDoc = await this.db!.documents.findOne({
      selector: {
        id: document.id
      }
    }).exec();

    if (existingDoc) {
      // Обновляем существующий документ с обработкой ошибок
      try {
        // Проверяем, что не перезаписываем контент пустой строкой
        const updateData: any = {
          localTimestamp: now,
          lastAccessed: now,
          syncStatus: 'pending' as SyncStatus
        };

        // Добавляем content только если он действительно предоставлен
        if (document.content !== undefined && document.content !== null) {
          updateData.content = document.content;
          console.log(`[DocumentRepository] Обновляем контент существующего документа ${document.id}`);
        } else {
          console.warn(`[DocumentRepository] Контент не предоставлен для обновления документа ${document.id}, оставляем существующий`);
        }

        // Добавляем другие поля, если они предоставлены
        if (document.serverTimestamp !== undefined) {
          updateData.serverTimestamp = document.serverTimestamp;
        }

        await existingDoc.incrementalUpdate({
          $set: updateData
        });
        return existingDoc.toJSON();
      } catch (updateError) {
        console.warn(`Не удалось обновить существующий документ ${document.id}:`, updateError);
        // Если не удалось обновить, попробуем получить latest версию
        const latestDoc = existingDoc.getLatest();
        return latestDoc.toJSON();
      }
    } else {
      // Проверяем количество документов и удаляем старые при необходимости
      await cleanupLRUDocuments(this.db!);

      // Проверяем, что контент не пустой для нового документа
      if (!document.content && document.content !== '') {
        console.error(`[DocumentRepository] Попытка создать новый документ ${document.id} с пустым контентом!`);
        throw new Error(`Невозможно создать документ без контента`);
      }

      console.log(`[DocumentRepository] Создаем новый документ ${document.id} с контентом`);

      // Создаем новый документ
      const newDoc = await this.db!.documents.insert({
        id: document.id,
        content: document.content,
        localTimestamp: now,
        serverTimestamp: document.serverTimestamp || now,
        lastAccessed: now,
        syncStatus: 'pending' as SyncStatus
      });

      return newDoc.toJSON();
    }
  }

  /**
   * Обновляет статус синхронизации документа
   * @param id ID документа
   * @param syncStatus Новый статус синхронизации
   * @param serverTimestamp Время на сервере
   * @returns Обновленный документ или null, если документ не найден
   */
  async updateSyncStatus(
    id: string,
    syncStatus: SyncStatus,
    serverTimestamp?: number
  ): Promise<DocumentSchemaType | null> {
    if (!this.db) {
      await this.initialize();
    }

    const doc = await this.db!.documents.findOne({
      selector: {
        id
      }
    }).exec();

    if (!doc) {
      return null;
    }

    const updateObj: any = {
      syncStatus
    };

    if (serverTimestamp) {
      updateObj.serverTimestamp = serverTimestamp;
    }

    try {
      // Используем incremental update для предотвращения конфликтов
      await doc.incrementalUpdate({
        $set: updateObj
      });
    } catch (updateError) {
      console.warn(`Не удалось обновить статус синхронизации для документа ${id}:`, updateError);
      // Возвращаем документ без обновления
    }

    return doc.toJSON();
  }

  /**
   * Получает все документы с указанным статусом синхронизации
   * @param syncStatus Статус синхронизации
   * @returns Массив документов
   */
  async getDocumentsBySyncStatus(syncStatus: SyncStatus): Promise<DocumentSchemaType[]> {
    if (!this.db) {
      await this.initialize();
    }

    const docs = await this.db!.documents.find({
      selector: {
        syncStatus
      }
    }).exec();

    return docs.map(doc => doc.toJSON());
  }


}

// Экземпляр репозитория для использования в приложении
export const documentRepository = new DocumentRepository(); 